import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBackOutlined as ArrowBackOutlinedIcon, AssignmentTurnedInOutlined as AssignmentTurnedInOutlinedIcon, LocalShippingOutlined as LocalShippingOutlinedIcon, CloudUploadOutlined as CloudUploadOutlinedIcon, Close as CloseIcon, CheckCircleOutlineRounded as CheckCircleOutlineRoundedIcon, HighlightOffRounded as HighlightOffRoundedIcon, NotificationsActiveOutlined as NotificationsActiveOutlinedIcon } from "../../../components/icons";
import { Diversity3Outlined as Diversity3OutlinedIcon, Inventory2Outlined as Inventory2OutlinedIcon } from "../../../components/icons";
import { PendingActionsOutlined as PendingActionsOutlinedIcon, CheckCircleOutlineOutlined as CheckCircleOutlineOutlinedIcon, AutorenewOutlined as AutorenewOutlinedIcon, Search as SearchIcon } from "../../../components/icons";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  acknowledgeBodyMovement,
  decideMovementApproval,
  getBodyDispatch,
  getBodyDispatches,
  markBodyDispatchEasipolUploaded,
  receiveInternalTransferDispatch,
  requestMovementApproval,
  submitInternalTransferDispatch,
} from "../../../services/dashboard";
import Loader from "../../../components/Loader";
import { useSessionStore } from "../../../store/useSessionStore";
import { useUserStore } from "../../../store/useUserStore";
import {
  formatOperationsStatus,
  ifasColors,
  OperationsDetailField,
  OperationsDetailGrid,
  OperationsEmptyState,
  OperationsPageShell,
  OperationsSectionHeader,
  operationsDetailPaperSx,
} from "../ifas/components/OperationsUI";
import {
  STANDARD_TONES,
  StandardListCardHead,
  StandardMetricCard,
  StandardPager,
  StatusDotChip,
  standardClickableRowSx,
  standardListCardSx,
  standardListCellPrimarySx,
  standardListCellSecondarySx,
  standardListGhostBtnSx,
  standardListHeadCellSx,
  standardListScrollSx,
  standardOutlineBtnSx,
  standardRowActionBtnSx,
} from "../ifas/components/StandardUI";
import { canAccessIfasModule } from "../../../utils/ifasAccess";

// Dispatch statuses are passed to StatusDotChip explicitly. getOperationsStatusSx
// matches on exact equality, so seven of these nine match nothing and render
// grey today. The shared standardStatusTone inference is no better here, and it
// is imported by the live claims tree, so it is neither used nor modified.
// Must stay in step with the `size` the list fetch sends, or the pager
// computes a page count the server never had.
const QUEUE_PAGE_SIZE = 10;

const DISPATCH_STATUS_TONE = {
  DRAFT: "amber",
  PENDING_SUBMISSION: "amber",
  TRANSFER_INITIATED: "blue",
  TRANSFER_ACCEPTED: "blue",
  EXTERNAL_RELEASE_PENDING: "amber",
  RELEASED_TO_EXTERNAL_PARLOUR: "green",
  FAMILY_DELIVERY_PENDING: "amber",
  DELIVERED_TO_FAMILY: "green",
  CANCELLED: "red",
} as const;

const dispatchStatusTone = (status?: string) =>
  DISPATCH_STATUS_TONE[status as keyof typeof DISPATCH_STATUS_TONE] ?? "grey";

const DISPATCH_TYPES = [
  { value: "", label: "All types" },
  { value: "INTERNAL_TRANSFER", label: "Internal Transfer" },
  { value: "EXTERNAL_PARLOUR_RELEASE", label: "External Parlour" },
  { value: "FAMILY_DELIVERY", label: "Delivery To Family" },
];

const STATUS_OPTIONS = [
  "",
  "DRAFT",
  "PENDING_SUBMISSION",
  "TRANSFER_INITIATED",
  "TRANSFER_ACCEPTED",
  "EXTERNAL_RELEASE_PENDING",
  "RELEASED_TO_EXTERNAL_PARLOUR",
  "FAMILY_DELIVERY_PENDING",
  "DELIVERED_TO_FAMILY",
  "CANCELLED",
];

const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).format("DD MMM YYYY HH:mm") : "Not captured";

const formatDate = (value?: string | null) =>
  value ? dayjs(value).format("DD MMM YYYY") : "Not captured";

const formatBool = (value?: boolean | null) =>
  value === true ? "Yes" : value === false ? "No" : "Not captured";

type DialogMode =
  | null
  | "REQUEST_APPROVAL"
  | "APPROVE_MOVEMENT"
  | "REJECT_MOVEMENT"
  | "ACKNOWLEDGE_MOVEMENT"
  | "SUBMIT_TRANSFER"
  | "RECEIVE_TRANSFER"
  | "MARK_EASIPOL";

// Icon and color treatment per dialog action.
const getDialogVisual = (mode: DialogMode) => {
  switch (mode) {
    case "APPROVE_MOVEMENT":
      return { icon: <CheckCircleOutlineRoundedIcon />, color: STANDARD_TONES.green.color, bg: STANDARD_TONES.green.bg };
    case "REJECT_MOVEMENT":
      return { icon: <HighlightOffRoundedIcon />, color: STANDARD_TONES.red.color, bg: STANDARD_TONES.red.bg };
    case "ACKNOWLEDGE_MOVEMENT":
      return { icon: <NotificationsActiveOutlinedIcon />, color: STANDARD_TONES.blue.color, bg: STANDARD_TONES.blue.bg };
    case "SUBMIT_TRANSFER":
      return { icon: <LocalShippingOutlinedIcon />, color: STANDARD_TONES.orange.color, bg: STANDARD_TONES.orange.bg };
    case "RECEIVE_TRANSFER":
      return { icon: <Inventory2OutlinedIcon />, color: STANDARD_TONES.orange.color, bg: STANDARD_TONES.orange.bg };
    case "MARK_EASIPOL":
      return { icon: <CloudUploadOutlinedIcon />, color: STANDARD_TONES.blue.color, bg: STANDARD_TONES.blue.bg };
    case "REQUEST_APPROVAL":
    default:
      return { icon: <AssignmentTurnedInOutlinedIcon />, color: STANDARD_TONES.orange.color, bg: STANDARD_TONES.orange.bg };
  }
};

const getDialogTitle = (mode: DialogMode) => {
  switch (mode) {
    case "REQUEST_APPROVAL":
      return "Request Exception Approval";
    case "APPROVE_MOVEMENT":
      return "Approve Exception";
    case "REJECT_MOVEMENT":
      return "Reject Exception";
    case "ACKNOWLEDGE_MOVEMENT":
      return "Acknowledge Exception";
    case "SUBMIT_TRANSFER":
      return "Dispatch Internal Transfer";
    case "RECEIVE_TRANSFER":
      return "Receive At Mortuary";
    case "MARK_EASIPOL":
      return "Mark Easipol Upload";
    default:
      return "Body Dispatch Action";
  }
};

const getDialogDescription = (mode: DialogMode) => {
  switch (mode) {
    case "REQUEST_APPROVAL":
      return "Send the exception to the Operations Manager role queue before dispatch can continue.";
    case "APPROVE_MOVEMENT":
      return "Approve this exception so the Mortuary Supervisor can continue the dispatch workflow.";
    case "REJECT_MOVEMENT":
      return "Reject this exception and capture the reason so the team can correct the record.";
    case "ACKNOWLEDGE_MOVEMENT":
      return "Record that the exception outcome has been acknowledged by the responsible team.";
    case "SUBMIT_TRANSFER":
      return "Dispatch the body for the internal transfer after all transfer requirements are complete.";
    case "RECEIVE_TRANSFER":
      return "Capture receiving mortuary details, mileage-in, storage and supervisor sign-off.";
    case "MARK_EASIPOL":
      return "Record that the completed dispatch form has been uploaded to Easipol.";
    default:
      return "";
  }
};

const getDialogConfirmLabel = (mode: DialogMode) => {
  switch (mode) {
    case "REQUEST_APPROVAL":
      return "Send Request";
    case "APPROVE_MOVEMENT":
      return "Approve";
    case "REJECT_MOVEMENT":
      return "Reject";
    case "ACKNOWLEDGE_MOVEMENT":
      return "Acknowledge";
    case "SUBMIT_TRANSFER":
      return "Dispatch";
    case "RECEIVE_TRANSFER":
      return "Receive";
    case "MARK_EASIPOL":
      return "Mark Uploaded";
    default:
      return "Confirm";
  }
};

type BodyDispatchPortalProps = {
  dispatchNumber?: string;
  onSelectDispatch?: (dispatchNumber: string) => void;
  onBack?: () => void;
  onMortuaryClicked?: () => void;
};

export default function BodyDispatchPortal({
  dispatchNumber,
  onSelectDispatch,
  onBack,
  onMortuaryClicked,
}: BodyDispatchPortalProps) {
  const { userName, userRole } = useSessionStore();
  const storedRole = useUserStore((state) => state.role);
  const effectiveRole = userRole || storedRole;
  const normalizedRole = String(effectiveRole || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  const isAdminRole = ["ifas_administrator", "ifas_system_administrator", "system_administrator", "administrator"].includes(normalizedRole);
  const isOperationsManagerRole = ["operations_manager", "ifas_operations_manager"].includes(normalizedRole);
  const isMortuarySupervisorRole = ["mortuary_supervisor", "ifas_mortuary_supervisor"].includes(normalizedRole);
  const canApproveOperationsException = isAdminRole || isOperationsManagerRole;
  const canExecuteDispatch = isAdminRole || isMortuarySupervisorRole;
  const canViewBodyDispatch = canAccessIfasModule(effectiveRole, "bodyDispatch");
  const isDetailsView = Boolean(dispatchNumber);
  const [dispatches, setDispatches] = useState<any[]>([]);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [dispatchTotal, setDispatchTotal] = useState(0);
  const [dispatchTypeFilter, setDispatchTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [selectedDispatch, setSelectedDispatch] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [formState, setFormState] = useState<any>({});
  const [saving, setSaving] = useState(false);

  const resetDialog = () => {
    setDialogMode(null);
    setFormState({});
  };

  const loadDispatches = useCallback(async () => {
    setLoading(true);
    const response = await getBodyDispatches({
      dispatchType: dispatchTypeFilter || undefined,
      status: statusFilter || undefined,
      searchText: searchText || undefined,
      page,
      size: QUEUE_PAGE_SIZE,
    });

    if (response?.status === 200) {
      const payload = response.data || {};
      setDispatches(payload.dispatches || []);
      setStatusCounts(payload.statusCounts || {});
      setDispatchTotal(Number(payload.totalCount || 0));
    } else {
      toast.error(response?.data?.message || "Failed to load body dispatch records");
    }
    setLoading(false);
  }, [dispatchTypeFilter, statusFilter, searchText, page]);

  const loadDispatch = useCallback(async (selectedDispatchNumber: string) => {
    if (!selectedDispatchNumber) return;
    setDetailLoading(true);
    const response = await getBodyDispatch(selectedDispatchNumber);
    if (response?.status === 200) {
      setSelectedDispatch(response.data);
    } else {
      toast.error(response?.data?.message || "Failed to load body dispatch details");
    }
    setDetailLoading(false);
  }, []);

  useEffect(() => {
    if (!isDetailsView) {
      loadDispatches();
    }
  }, [isDetailsView, loadDispatches]);

  useEffect(() => {
    if (isDetailsView && dispatchNumber) {
      loadDispatch(dispatchNumber);
    } else if (!isDetailsView) {
      setSelectedDispatch(null);
    }
  }, [isDetailsView, dispatchNumber, loadDispatch]);

  const refreshCurrentView = async () => {
    if (isDetailsView && dispatchNumber) {
      await loadDispatch(dispatchNumber);
    } else {
      await loadDispatches();
    }
  };

  const openDialog = (mode: DialogMode) => {
    if (mode === "MARK_EASIPOL") {
      setFormState({
        uploadedBy: userName || "",
        uploadedAt: dayjs().format("YYYY-MM-DDTHH:mm"),
      });
    } else if (mode === "REQUEST_APPROVAL") {
      setFormState({
        performedBy: userName || "",
        notes: "",
        requesterEmail: "",
        approverEmail: "",
        slaMinutes: 120,
      });
    } else if (
      mode === "APPROVE_MOVEMENT" ||
      mode === "REJECT_MOVEMENT" ||
      mode === "ACKNOWLEDGE_MOVEMENT"
    ) {
      setFormState({
        performedBy: userName || "",
        notes: "",
      });
    } else {
      setFormState({
        performedBy: userName || "",
        receivingStorageLocation: selectedDispatch?.receivingStorageLocation || "",
        mileageIn: selectedDispatch?.mileageIn || "",
        receivingSupervisorName: selectedDispatch?.receivingSupervisorName || userName || "",
        receivingSupervisorSignature: selectedDispatch?.receivingSupervisorSignature || "",
        receivingSupervisorDate: selectedDispatch?.receivingSupervisorDate || dayjs().format("YYYY-MM-DD"),
      });
    }
    setDialogMode(mode);
  };

  const submitDialog = async () => {
    if (!selectedDispatch?.dispatchNumber || !dialogMode) return;
    setSaving(true);

    let response: any;
    if (dialogMode === "REQUEST_APPROVAL") {
      response = await requestMovementApproval(selectedDispatch.dispatchNumber, {
        performedBy: formState.performedBy,
        requesterEmail: formState.requesterEmail || undefined,
        approverEmail: formState.approverEmail || undefined,
        notes: formState.notes || undefined,
        slaMinutes: formState.slaMinutes ? Number(formState.slaMinutes) : undefined,
      });
    } else if (dialogMode === "APPROVE_MOVEMENT") {
      response = await decideMovementApproval(selectedDispatch.dispatchNumber, {
        performedBy: formState.performedBy,
        approved: true,
        notes: formState.notes || undefined,
      });
    } else if (dialogMode === "REJECT_MOVEMENT") {
      response = await decideMovementApproval(selectedDispatch.dispatchNumber, {
        performedBy: formState.performedBy,
        approved: false,
        notes: formState.notes || undefined,
      });
    } else if (dialogMode === "ACKNOWLEDGE_MOVEMENT") {
      response = await acknowledgeBodyMovement(selectedDispatch.dispatchNumber, {
        performedBy: formState.performedBy,
        notes: formState.notes || undefined,
      });
    } else if (dialogMode === "SUBMIT_TRANSFER") {
      response = await submitInternalTransferDispatch(selectedDispatch.dispatchNumber, {
        performedBy: formState.performedBy,
      });
    } else if (dialogMode === "RECEIVE_TRANSFER") {
      response = await receiveInternalTransferDispatch(selectedDispatch.dispatchNumber, {
        performedBy: formState.performedBy,
        receivingStorageLocation: formState.receivingStorageLocation || undefined,
        mileageIn: formState.mileageIn === "" ? undefined : Number(formState.mileageIn),
        receivingSupervisorName: formState.receivingSupervisorName || undefined,
        receivingSupervisorSignature: formState.receivingSupervisorSignature || undefined,
        receivingSupervisorDate: formState.receivingSupervisorDate || undefined,
      });
    } else if (dialogMode === "MARK_EASIPOL") {
      response = await markBodyDispatchEasipolUploaded(selectedDispatch.dispatchNumber, {
        uploadedBy: formState.uploadedBy,
        uploadedAt: formState.uploadedAt,
      });
    }

    if (response?.status === 200) {
      toast.success(response.data?.message || "Dispatch action completed successfully");
      resetDialog();
      await refreshCurrentView();
    } else {
      toast.error(response?.data?.message || "Dispatch action failed");
    }
    setSaving(false);
  };

  const selectedMeta = useMemo(
    () => [
      {
        label: "Draft / Pending",
        value:
          (statusCounts.DRAFT || 0) +
          (statusCounts.PENDING_SUBMISSION || 0) +
          (statusCounts.EXTERNAL_RELEASE_PENDING || 0) +
          (statusCounts.FAMILY_DELIVERY_PENDING || 0),
        caption: "awaiting action",
        tone: "amber" as const,
        icon: <PendingActionsOutlinedIcon />,
        // Multi-status bucket (4 statuses) — the controller takes one enum,
        // so this card stays inert per Samuel's ruling rather than fan out or
        // apply only its first status.
        filterValue: undefined,
      },
      {
        label: "Transfer Active",
        value:
          (statusCounts.TRANSFER_INITIATED || 0) + (statusCounts.TRANSFER_ACCEPTED || 0),
        caption: "in transit",
        tone: "blue" as const,
        icon: <LocalShippingOutlinedIcon />,
        // Inert, like the other multi-status buckets. Alyysa flagged that this
        // card aggregates TRANSFER_INITIATED + TRANSFER_ACCEPTED against the
        // same single-enum controller constraint, and she was right — the
        // "five single-status cards" in her brief was my miscount; there are
        // four of each. Samuel's ruling explicitly rejected having a bucket
        // apply only its first status, so this one stays unwired until the
        // endpoint can take a status list.
        filterValue: undefined,
      },
      {
        label: "External Releases",
        value: statusCounts.RELEASED_TO_EXTERNAL_PARLOUR || 0,
        caption: "released to parlour",
        tone: "green" as const,
        icon: <CheckCircleOutlineOutlinedIcon />,
        filterValue: "RELEASED_TO_EXTERNAL_PARLOUR",
      },
      {
        label: "Family Deliveries",
        value: statusCounts.DELIVERED_TO_FAMILY || 0,
        caption: "handed to family",
        tone: "green" as const,
        icon: <Diversity3OutlinedIcon />,
        filterValue: "DELIVERED_TO_FAMILY",
      },
    ],
    [statusCounts],
  );

  const canSubmitTransfer =
    selectedDispatch?.dispatchType === "INTERNAL_TRANSFER" &&
    selectedDispatch?.status === "DRAFT" &&
    selectedDispatch?.movementApprovalStatus === "APPROVED" &&
    canExecuteDispatch;
  const canReceiveTransfer =
    selectedDispatch?.dispatchType === "INTERNAL_TRANSFER" &&
    selectedDispatch?.status === "TRANSFER_INITIATED" &&
    canExecuteDispatch;
  const requiresMovementApproval =
    selectedDispatch?.dispatchType === "INTERNAL_TRANSFER" ||
    Boolean(selectedDispatch?.bodyInBadState);
  const movementApprovalStatus = String(selectedDispatch?.movementApprovalStatus || "").toUpperCase();
  const hasApprovedMovementException =
    movementApprovalStatus === "APPROVED";
  const isMovementApprovalRejected =
    requiresMovementApproval && movementApprovalStatus === "REJECTED";
  const canRequestMovementApproval =
    (canExecuteDispatch || isAdminRole) &&
    selectedDispatch &&
    requiresMovementApproval &&
    ["DRAFT", "EXTERNAL_RELEASE_PENDING", "FAMILY_DELIVERY_PENDING"].includes(
      String(selectedDispatch.status || ""),
    ) &&
    !["PENDING", "APPROVED"].includes(
      movementApprovalStatus,
    );
  const canApproveMovementRequest =
    canApproveOperationsException &&
    movementApprovalStatus === "PENDING";
  const canRejectMovementRequest = canApproveMovementRequest;
  const canAcknowledgeMovement =
    canExecuteDispatch &&
    selectedDispatch &&
    !selectedDispatch?.movementAcknowledgedAt &&
    ["TRANSFER_ACCEPTED", "RELEASED_TO_EXTERNAL_PARLOUR", "DELIVERED_TO_FAMILY"].includes(
      String(selectedDispatch.status || ""),
    );
  const canMarkEasipol =
    selectedDispatch &&
    !selectedDispatch.easipolUploaded &&
    ["TRANSFER_ACCEPTED", "RELEASED_TO_EXTERNAL_PARLOUR", "DELIVERED_TO_FAMILY"].includes(
      selectedDispatch.status,
    );

  const renderDispatchSpecificDetails = () => {
    if (!selectedDispatch) return null;

    if (selectedDispatch.dispatchType === "INTERNAL_TRANSFER") {
      return (
        <>
          <OperationsSectionHeader title="Internal Transfer Details" />
          <OperationsDetailGrid>
            <OperationsDetailField label="Transfer Date / Time" value={formatDateTime(selectedDispatch.transferDateTime)} />
            <OperationsDetailField label="Departure Mortuary" value={selectedDispatch.departureMortuary} />
            <OperationsDetailField label="Receiving Mortuary" value={selectedDispatch.receivingMortuary} />
            <OperationsDetailField label="Receiving Storage Location" value={selectedDispatch.receivingStorageLocation} />
            <OperationsDetailField label="Family Viewed Body" value={formatBool(selectedDispatch.familyViewedConfirmed)} />
            <OperationsDetailField label="Belongings Handed Over" value={formatBool(selectedDispatch.clothesBelongingsHandedOver)} />
            <OperationsDetailField label="Clothes Transferred" value={selectedDispatch.clothesTransferred} />
            <OperationsDetailField label="Body In Bad State" value={formatBool(selectedDispatch.bodyInBadState)} />
            <OperationsDetailField label="Ops Escalated" value={formatBool(selectedDispatch.operationsManagerEscalated)} />
            <OperationsDetailField label="Ops Approved By" value={selectedDispatch.operationsManagerApprovedBy} />
            <OperationsDetailField label="Branches Agreed" value={formatBool(selectedDispatch.bothBranchesAgreed)} />
            <OperationsDetailField label="Vehicle Registration" value={selectedDispatch.transferVehicleRegistration} />
            <OperationsDetailField label="Dispatching Personnel" value={selectedDispatch.dispatchingPersonnelName} />
            <OperationsDetailField label="Undertaker" value={selectedDispatch.transferUndertakerName} />
            <OperationsDetailField label="Mileage Out" value={selectedDispatch.mileageOut} />
            <OperationsDetailField label="Mileage In" value={selectedDispatch.mileageIn} />
            <OperationsDetailField label="Receiving Supervisor" value={selectedDispatch.receivingSupervisorName} />
            <OperationsDetailField label="Supervisor Sign-off Date" value={formatDate(selectedDispatch.receivingSupervisorDate)} />
          </OperationsDetailGrid>
        </>
      );
    }

    if (selectedDispatch.dispatchType === "EXTERNAL_PARLOUR_RELEASE") {
      return (
        <>
          <OperationsSectionHeader title="External Parlour Release" />
          <OperationsDetailGrid>
            <OperationsDetailField label="Release Date / Time" value={formatDateTime(selectedDispatch.externalReleaseDateTime)} />
            <OperationsDetailField label="Branch" value={selectedDispatch.branchName} />
            <OperationsDetailField label="Parlour Name" value={selectedDispatch.parlourName} />
            <OperationsDetailField label="Parlour Contact" value={selectedDispatch.parlourContact} />
            <OperationsDetailField label="Driver Name" value={selectedDispatch.parlourDriverName} />
            <OperationsDetailField label="Driver Contact" value={selectedDispatch.parlourDriverContact} />
            <OperationsDetailField label="Driver ID Number" value={selectedDispatch.parlourDriverIdNumber} />
            <OperationsDetailField label="Federation Certificate" value={selectedDispatch.parlourFederationCertificateNumber} />
            <OperationsDetailField label="SAPFA Rate" value={selectedDispatch.sapfaRateAmount} />
            <OperationsDetailField label="Amount Paid" value={selectedDispatch.amountPaid} />
            <OperationsDetailField label="Finance Confirmed" value={formatBool(selectedDispatch.financePaymentConfirmed)} />
            <OperationsDetailField label="Finance Confirmed By" value={selectedDispatch.financeConfirmedBy} />
            <OperationsDetailField label="Branch Override" value={formatBool(selectedDispatch.branchManagerOverride)} />
            <OperationsDetailField label="Override By" value={selectedDispatch.branchManagerOverrideBy} />
            <OperationsDetailField label="Override Reason" value={selectedDispatch.branchManagerOverrideReason} />
            <OperationsDetailField label="SAPFA Tax Invoice" value={selectedDispatch.sapfaInvoiceNumber || "—"} />
            <OperationsDetailField label="Clothes With Body" value={selectedDispatch.clothesWithBody} />
            <OperationsDetailField label="Claimant Name" value={selectedDispatch.claimantName} />
            <OperationsDetailField label="Claimant ID" value={selectedDispatch.claimantIdNumber} />
            <OperationsDetailField label="Claimant Declaration" value={formatBool(selectedDispatch.claimantDeclarationAccepted)} />
            <OperationsDetailField label="Parlour Stamp" value={selectedDispatch.parlourStampImage ? "Uploaded" : "Not uploaded"} />
          </OperationsDetailGrid>
          <Box mt={2}>
            <OperationsSectionHeader title="Document Verification" />
            <Box display="flex" gap={1} flexWrap="wrap">
              {[
                ["Death Certificate", selectedDispatch.deathCertificateVerified],
                ["Deceased ID", selectedDispatch.deceasedIdVerified],
                ["Informant ID", selectedDispatch.informantIdVerified],
                ["DHA1663", selectedDispatch.dha1663Verified],
                ["Parlour Driver ID", selectedDispatch.parlourDriverIdVerified],
                ["Federation Certificate", selectedDispatch.federationCertificateVerified],
                ["Invoice", selectedDispatch.invoiceVerified],
                ["Proof Of Payment", selectedDispatch.proofOfPaymentVerified],
              ].map(([label, complete]) => (
                <Chip
                  key={String(label)}
                  label={String(label)}
                  size="small"
                  sx={{
                    bgcolor: complete ? STANDARD_TONES.green.bg : STANDARD_TONES.amber.bg,
                    color: complete ? STANDARD_TONES.green.color : STANDARD_TONES.amber.color,
                    fontWeight: 800,
                  }}
                />
              ))}
            </Box>
          </Box>
        </>
      );
    }

    return (
      <>
        <OperationsSectionHeader title="Family Delivery Details" />
        <OperationsDetailGrid>
          <OperationsDetailField label="Delivery Date / Time" value={formatDateTime(selectedDispatch.familyDeliveryDateTime)} />
          <OperationsDetailField label="Delivery Address" value={selectedDispatch.deliveryAddress} />
          <OperationsDetailField label="Via Address" value={selectedDispatch.viaAddress} />
          <OperationsDetailField label="Recipient Name" value={selectedDispatch.recipientName} />
          <OperationsDetailField label="Recipient ID Number" value={selectedDispatch.recipientIdNumber} />
          <OperationsDetailField label="Relationship" value={selectedDispatch.recipientRelationship} />
          <OperationsDetailField label="Undertaker" value={selectedDispatch.undertakerName} />
          <OperationsDetailField label="Vehicle Registration" value={selectedDispatch.vehicleRegistration} />
          <OperationsDetailField label="Casket Type" value={selectedDispatch.casketType} />
          <OperationsDetailField label="Casket Colour" value={selectedDispatch.casketColour} />
          <OperationsDetailField label="Mileage Out" value={selectedDispatch.familyDeliveryMileageOut} />
          <OperationsDetailField label="Feedback Due" value={formatDate(selectedDispatch.postFuneralFeedbackDueDate)} />
          <OperationsDetailField label="Body Readiness" value={formatBool(selectedDispatch.bodyReadinessConfirmed)} />
          <OperationsDetailField label="Family Viewing" value={formatBool(selectedDispatch.familyViewingConfirmed)} />
          <OperationsDetailField label="Handles Explained" value={formatBool(selectedDispatch.handlesExplained)} />
        </OperationsDetailGrid>
        <Box mt={2}>
          <OperationsSectionHeader title="Delivery Verification" />
          <Box display="flex" gap={1} flexWrap="wrap">
            {[
              ["Original Death Certificate", selectedDispatch.originalDeathCertificateVerified],
              ["Beneficiary ID", selectedDispatch.beneficiaryIdVerified],
              ["Body Readiness", selectedDispatch.bodyReadinessConfirmed],
              ["Family Viewing", selectedDispatch.familyViewingConfirmed],
              ["Personal Effects Sign-off", selectedDispatch.personalEffectsSignedOff],
              ["Clothes Returned", selectedDispatch.clothesReturned],
              ["Cellphone / Jewellery Returned", selectedDispatch.cellphoneJewelleryReturned],
              ["Coffin Spray Checked", selectedDispatch.coffinSprayConfirmed],
              ["Photo / Programme Checked", selectedDispatch.photoProgrammeConfirmed],
              ["Cross / Plaque Checked", selectedDispatch.crossPlaqueConfirmed],
              ["Handles Explained", selectedDispatch.handlesExplained],
            ].map(([label, complete]) => (
              <Chip
                key={String(label)}
                label={String(label)}
                size="small"
                sx={{
                  bgcolor: complete ? STANDARD_TONES.green.bg : STANDARD_TONES.amber.bg,
                  color: complete ? STANDARD_TONES.green.color : STANDARD_TONES.amber.color,
                  fontWeight: 800,
                }}
              />
            ))}
          </Box>
        </Box>
      </>
    );
  };

  if (!canViewBodyDispatch) {
    return (
      <OperationsPageShell
        eyebrow="Body Dispatch"
        title="Body Dispatch"
        description="This workspace is restricted by your IFAS role."
      >
        <OperationsEmptyState
          title="No body dispatch access"
          description="Ask an IFAS administrator or mortuary supervisor if you should work in this module."
        />
      </OperationsPageShell>
    );
  }

  if (!isDetailsView) {
    return (
      <OperationsPageShell
        eyebrow="Body Dispatch"
        title="Body Dispatch Queue"
        description="Review all post-mortuary dispatch work first, then open a record to complete internal transfer, external parlour release, or family delivery."
        actions={
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="text"
              onClick={() => onMortuaryClicked?.()}
              sx={{ ...standardOutlineBtnSx, minHeight: 35, fontSize: "12.5px", lineHeight: 1.4, px: 1.6 }}
            >
              Open Mortuary
            </Button>
            <Button
              variant="text"
              startIcon={<AutorenewOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={() => loadDispatches()}
              sx={standardListGhostBtnSx}
            >
              Refresh
            </Button>
          </Box>
        }
      >
        <Box display="grid" gap={2}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              },
              gap: 2,
              mb: 2.5,
            }}
          >
            {selectedMeta.map((item) => {
              const filterValue = item.filterValue;
              return (
                <StandardMetricCard
                  key={item.label}
                  label={item.label}
                  value={item.value}
                  caption={item.caption}
                  tone={item.tone}
                  icon={item.icon}
                  compact
                  {...(filterValue
                    ? {
                        onClick: () => {
                          setStatusFilter((prev) => (prev === filterValue ? "" : filterValue));
                          setPage(0);
                        },
                        selected: statusFilter === filterValue,
                      }
                    : {})}
                />
              );
            })}
          </Box>

          <Paper
            elevation={0}
            sx={{ display: "flex", flexWrap: "wrap", gap: 1, bgcolor: ifasColors.surface, border: `1px solid ${ifasColors.border}`, borderRadius: "12px", p: "0.6rem" }}
          >
            <TextField
              size="small"
              placeholder="Dispatch no, case, deceased, target"
              aria-label="Search dispatch queue"
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: ifasColors.muted }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: "1 1 280px", minWidth: 280, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: ifasColors.surface } }}
            />
            <TextField
              size="small"
              select
              aria-label="Filter by dispatch type"
              value={dispatchTypeFilter}
              onChange={(event) => {
                setDispatchTypeFilter(event.target.value);
                setPage(0);
              }}
              SelectProps={{ displayEmpty: true }}
              sx={{ minWidth: 190, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: ifasColors.surface } }}
            >
              {DISPATCH_TYPES.map((option) => (
                <MenuItem key={option.value || "ALL"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(0);
              }}
              SelectProps={{ displayEmpty: true }}
              sx={{ minWidth: 190, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: ifasColors.surface } }}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option || "ALL"} value={option}>
                  {option ? formatOperationsStatus(option) : "All statuses"}
                </MenuItem>
              ))}
            </TextField>
          </Paper>

          <Paper elevation={0} sx={standardListCardSx}>
            {loading ? <Loader isLoading={loading} /> : null}
            <StandardListCardHead title="Dispatches" count={dispatchTotal} />
            <Box sx={standardListScrollSx}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={standardListHeadCellSx}>Dispatch</TableCell>
                    <TableCell sx={standardListHeadCellSx}>Case / deceased</TableCell>
                    <TableCell sx={standardListHeadCellSx}>Target</TableCell>
                    <TableCell sx={standardListHeadCellSx}>Status</TableCell>
                    <TableCell sx={standardListHeadCellSx} align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dispatches.length === 0 && !loading ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <OperationsEmptyState
                          title="No dispatches found"
                          description={
                            searchText || dispatchTypeFilter || statusFilter
                              ? "No dispatch matches these filters. Clear the search or choose a different type or status."
                              : "Create internal transfer, external parlour, or family delivery work from mortuary details first."
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    dispatches.map((item: any) => (
                      <TableRow
                        key={item.dispatchNumber}
                        tabIndex={0}
                        aria-label={`Open dispatch ${item.dispatchNumber}`}
                        sx={standardClickableRowSx}
                        onClick={() => onSelectDispatch?.(item.dispatchNumber)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onSelectDispatch?.(item.dispatchNumber);
                          }
                        }}
                      >
                        <TableCell>
                          <Typography component="span" sx={standardListCellPrimarySx}>{item.dispatchNumber}</Typography>
                          <Typography component="span" sx={standardListCellSecondarySx}>{formatOperationsStatus(item.dispatchType)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography component="span" sx={standardListCellPrimarySx}>{item.caseNumber || "System Case"}</Typography>
                          <Typography component="span" sx={standardListCellSecondarySx}>{item.deceasedFullNames}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography component="span" sx={standardListCellPrimarySx}>{item.targetName || item.mortuaryName}</Typography>
                        </TableCell>
                        <TableCell>
                          <StatusDotChip status={item.status} tone={dispatchStatusTone(item.status)} compact />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="text"
                            sx={standardRowActionBtnSx}
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectDispatch?.(item.dispatchNumber);
                            }}
                          >
                            Open
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
            <StandardPager page={page} total={dispatchTotal} pageSize={QUEUE_PAGE_SIZE} onPage={setPage} />
          </Paper>
        </Box>
      </OperationsPageShell>
    );
  }

  return (
    <OperationsPageShell
      eyebrow="Body Dispatch"
      title="Body Dispatch Details"
      description="Open one dispatch record at a time to complete the correct post-mortuary release or delivery workflow."
      actions={
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="text"
            startIcon={<ArrowBackOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={() => onBack?.()}
            sx={standardListGhostBtnSx}
          >
            Back To Dispatch Queue
          </Button>
          <Button
            variant="text"
            onClick={() => onMortuaryClicked?.()}
            sx={{ ...standardOutlineBtnSx, minHeight: 35, fontSize: "12.5px", lineHeight: 1.4, px: 1.6 }}
          >
            Open Mortuary
          </Button>
          <Button
            variant="text"
            onClick={() => refreshCurrentView()}
            sx={standardListGhostBtnSx}
          >
            Refresh
          </Button>
        </Box>
      }
    >
      <Paper elevation={0} sx={operationsDetailPaperSx}>
        {detailLoading ? <Loader isLoading={detailLoading} /> : null}
        {detailLoading ? (
          <Box minHeight={160} />
        ) : !selectedDispatch ? (
          <OperationsEmptyState
            title="No dispatch selected"
            description="Choose a body dispatch record from the queue to continue."
          />
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" gap={1.5} flexWrap="wrap">
              <Box>
                <Typography fontSize={24} fontWeight={900} color={ifasColors.text}>
                  {selectedDispatch.dispatchNumber}
                </Typography>
                <Typography fontSize={13.5} color={ifasColors.muted} mt={0.35}>
                  {formatOperationsStatus(selectedDispatch.dispatchType)}
                </Typography>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                <StatusDotChip
                  status={selectedDispatch.status}
                  tone={dispatchStatusTone(selectedDispatch.status)}
                />
                {selectedDispatch.easipolUploaded && (
                  <Chip
                    size="small"
                    label="Easipol Uploaded"
                    sx={{
                      bgcolor: STANDARD_TONES.green.bg,
                      color: STANDARD_TONES.green.color,
                      fontWeight: 800,
                    }}
                  />
                )}
              </Box>
            </Box>

            <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
              {canRequestMovementApproval && (
                <Button
                  variant="contained"
                  onClick={() => openDialog("REQUEST_APPROVAL")}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    bgcolor: "#24262B",
                    color: ifasColors.surface,
                    "&:hover": { bgcolor: "#111827", color: ifasColors.surface },
                  }}
                >
                  Request Exception Approval
                </Button>
              )}
              {canApproveMovementRequest && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => openDialog("APPROVE_MOVEMENT")}
                  sx={{ textTransform: "none", borderRadius: 2, color: ifasColors.surface }}
                >
                  Approve Exception
                </Button>
              )}
              {canRejectMovementRequest && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => openDialog("REJECT_MOVEMENT")}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    bgcolor: ifasColors.surface,
                    "&:hover": { bgcolor: ifasColors.softOrange },
                  }}
                >
                  Reject Exception
                </Button>
              )}
              {canSubmitTransfer && (
                <Button
                  variant="contained"
                  onClick={() => openDialog("SUBMIT_TRANSFER")}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Dispatch Transfer
                </Button>
              )}
              {canReceiveTransfer && (
                <Button
                  variant="contained"
                  onClick={() => openDialog("RECEIVE_TRANSFER")}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Receive At Mortuary
                </Button>
              )}
              {canMarkEasipol && (
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadOutlinedIcon />}
                  onClick={() => openDialog("MARK_EASIPOL")}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Mark Easipol Uploaded
                </Button>
              )}
              {canAcknowledgeMovement && (
                <Button
                  variant="outlined"
                  onClick={() => openDialog("ACKNOWLEDGE_MOVEMENT")}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Acknowledge Exception
                </Button>
              )}
            </Box>

            {requiresMovementApproval && (
              <Box
                mt={1.5}
                sx={{
                  border: `1px solid ${
                    hasApprovedMovementException
                      ? `${STANDARD_TONES.green.color}33`
                      : isMovementApprovalRejected
                        ? `${STANDARD_TONES.red.color}33`
                        : `${STANDARD_TONES.orange.color}33`
                  }`,
                  bgcolor: hasApprovedMovementException
                    ? STANDARD_TONES.green.bg
                    : isMovementApprovalRejected
                      ? STANDARD_TONES.red.bg
                      : STANDARD_TONES.orange.bg,
                  borderRadius: 2,
                  px: 1.75,
                  py: 1.25,
                }}
              >
                <Typography fontSize={13.5} fontWeight={900} color={ifasColors.text}>
                  {hasApprovedMovementException
                    ? "Operations approval captured"
                    : isMovementApprovalRejected
                      ? "Operations approval rejected"
                      : movementApprovalStatus === "PENDING"
                        ? "Waiting for Operations Manager approval"
                        : canApproveOperationsException
                          ? "Waiting for Mortuary Supervisor to request approval"
                        : "Operations Manager approval required before dispatch"}
                </Typography>
                <Typography fontSize={12.5} color={ifasColors.muted} mt={0.35}>
                  {canApproveOperationsException && !movementApprovalStatus
                    ? "Operations Manager can approve/reject only after the Mortuary Supervisor raises the exception approval request."
                    : "Internal transfer cannot be dispatched until the exception approval is approved. This record is visible here for approval tracking only."}
                </Typography>
              </Box>
            )}

            <Box
              mt={1.5}
              sx={{
                border: `1px solid ${ifasColors.border}`,
                bgcolor: ifasColors.page,
                borderRadius: 2,
                px: 1.5,
                py: 1,
              }}
            >
              <Typography fontSize={12.5} color={ifasColors.muted}>
                {selectedDispatch.dispatchType === "EXTERNAL_PARLOUR_RELEASE"
                  ? "External parlour release is completed from the Mortuary Annexure E form. Body Dispatch keeps the audit record and Easipol upload status."
                  : selectedDispatch.dispatchType === "FAMILY_DELIVERY"
                    ? "Family delivery is completed from the Mortuary Annexure F form. Body Dispatch keeps the audit record and Easipol upload status."
                  : "Prepared in Mortuary. Complete the physical handover, receiving, exception approval, and Easipol upload here."}
              </Typography>
            </Box>

            <Box mt={2}>
              <OperationsSectionHeader title="Dispatch Overview" />
              <OperationsDetailGrid>
                <OperationsDetailField label="Body Removal Number" value={selectedDispatch.bodyRemovalNumber} />
                <OperationsDetailField label="Body Number" value={selectedDispatch.bodyNumber} />
                <OperationsDetailField label="Case Number" value={selectedDispatch.caseNumber} />
                <OperationsDetailField label="Mortuary" value={selectedDispatch.mortuaryName} />
                <OperationsDetailField label="Funeral File Number" value={selectedDispatch.funeralFileNumber} />
                <OperationsDetailField label="Deceased" value={selectedDispatch.deceasedFullNames} />
                <OperationsDetailField label="Deceased ID" value={selectedDispatch.deceasedIdNumber} />
                <OperationsDetailField label="Family Representative" value={selectedDispatch.familyRepresentativeName} />
                <OperationsDetailField label="Family Representative ID" value={selectedDispatch.familyRepresentativeIdNumber} />
                <OperationsDetailField label="Relationship" value={selectedDispatch.relationshipToDeceased} />
                <OperationsDetailField label="Policy / Invoice Number" value={selectedDispatch.policyOrInvoiceNumber} />
                <OperationsDetailField label="Created By" value={selectedDispatch.createdBy} />
                <OperationsDetailField label="Created At" value={formatDateTime(selectedDispatch.createdAt)} />
                <OperationsDetailField label="Submitted At" value={formatDateTime(selectedDispatch.submittedAt)} />
                <OperationsDetailField label="Completed At" value={formatDateTime(selectedDispatch.completedAt)} />
              </OperationsDetailGrid>
            </Box>

            {(requiresMovementApproval || selectedDispatch.movementApprovalStatus) && (
              <Box mt={2}>
                <OperationsSectionHeader title="Exception Approval" />
                <OperationsDetailGrid>
                  <OperationsDetailField
                    label="Approval Status"
                    value={selectedDispatch.movementApprovalStatus || "Not requested"}
                  />
                  <OperationsDetailField
                    label="Requested By"
                    value={selectedDispatch.movementApprovalRequestedBy}
                  />
                  <OperationsDetailField
                    label="Requested At"
                    value={formatDateTime(selectedDispatch.movementApprovalRequestedAt)}
                  />
                  <OperationsDetailField
                    label="SLA Due At"
                    value={formatDateTime(selectedDispatch.movementApprovalSlaDueAt)}
                  />
                  <OperationsDetailField
                    label="Approved / Rejected By"
                    value={selectedDispatch.movementApprovedBy}
                  />
                  <OperationsDetailField
                    label="Decision At"
                    value={formatDateTime(selectedDispatch.movementApprovedAt)}
                  />
                  <OperationsDetailField
                    label="Approval Notes"
                    value={selectedDispatch.movementApprovalNotes}
                    span={2}
                  />
                  <OperationsDetailField
                    label="Acknowledged By"
                    value={selectedDispatch.movementAcknowledgedBy}
                  />
                  <OperationsDetailField
                    label="Acknowledged At"
                    value={formatDateTime(selectedDispatch.movementAcknowledgedAt)}
                  />
                  <OperationsDetailField
                    label="Acknowledgement Notes"
                    value={selectedDispatch.movementAcknowledgementNotes}
                    span={2}
                  />
                </OperationsDetailGrid>
              </Box>
            )}

            <Box mt={2}>{renderDispatchSpecificDetails()}</Box>

            {selectedDispatch.notes && (
              <Box mt={2}>
                <OperationsSectionHeader title="Notes" />
                <Typography fontSize={13.5} color={ifasColors.text} lineHeight={1.65}>
                  {selectedDispatch.notes}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>

      <Dialog
        open={Boolean(dialogMode)}
        onClose={resetDialog}
        maxWidth={
          dialogMode === "REQUEST_APPROVAL" || dialogMode === "RECEIVE_TRANSFER" ? "sm" : "xs"
        }
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(15, 23, 42, 0.5)",
              backdropFilter: "blur(6px)",
            },
          },
          paper: {
            sx: {
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 28px 70px rgba(15, 23, 42, 0.34)",
              border: "1px solid rgba(255,255,255,0.6)",
              maxWidth:
                dialogMode === "REQUEST_APPROVAL" || dialogMode === "RECEIVE_TRANSFER"
                  ? 640
                  : 520,
            },
          },
        }}
      >
        {dialogMode &&
          (() => {
            const visual = getDialogVisual(dialogMode);
            return (
              <>
                <DialogTitle
                  sx={{
                    px: 2.75,
                    py: 2.25,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    borderBottom: `1px solid ${ifasColors.border}`,
                    background: `linear-gradient(180deg, ${visual.bg} 0%, ${ifasColors.surface} 100%)`,
                  }}
                >
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 2.25,
                      backgroundColor: ifasColors.surface,
                      border: `1px solid ${visual.color}33`,
                      color: visual.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: `0 4px 10px ${visual.color}22`,
                    }}
                  >
                    {visual.icon}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontSize={18} fontWeight={900} color={ifasColors.text} lineHeight={1.2}>
                      {getDialogTitle(dialogMode)}
                    </Typography>
                    <Typography fontSize={12.75} color={ifasColors.muted} mt={0.45} lineHeight={1.45}>
                      {getDialogDescription(dialogMode)}
                    </Typography>
                  </Box>

                  <IconButton
                    onClick={resetDialog}
                    size="small"
                    sx={{
                      color: ifasColors.muted,
                      "&:hover": { backgroundColor: ifasColors.page },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </DialogTitle>

                <DialogContent sx={{ px: 2.75, py: 2.35, backgroundColor: ifasColors.surface }}>
                  {selectedDispatch && (
                    <Box
                      sx={{
                        border: `1px solid ${ifasColors.border}`,
                        bgcolor: ifasColors.page,
                        borderRadius: 2.25,
                        px: 1.75,
                        py: 1.5,
                        mb: 2.25,
                      }}
                    >
                      <Typography fontSize={11.5} fontWeight={900} color={ifasColors.orange} textTransform="uppercase" letterSpacing={0.4}>
                        Dispatch context
                      </Typography>
                      <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1.25} mt={1}>
                        <Box>
                          <Typography fontSize={11.5} color={ifasColors.muted}>Dispatch</Typography>
                          <Typography fontSize={13.5} fontWeight={800}>{selectedDispatch.dispatchNumber}</Typography>
                        </Box>
                        <Box>
                          <Typography fontSize={11.5} color={ifasColors.muted}>Type</Typography>
                          <Typography fontSize={13.5} fontWeight={800}>{formatOperationsStatus(selectedDispatch.dispatchType)}</Typography>
                        </Box>
                        <Box>
                          <Typography fontSize={11.5} color={ifasColors.muted}>Case</Typography>
                          <Typography fontSize={13.5} fontWeight={800}>{selectedDispatch.caseNumber || "Not captured"}</Typography>
                        </Box>
                        <Box>
                          <Typography fontSize={11.5} color={ifasColors.muted}>Deceased</Typography>
                          <Typography fontSize={13.5} fontWeight={800}>{selectedDispatch.deceasedFullNames || "Not captured"}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  <Box
                    display="grid"
                    gap={1.75}
                    sx={{
                      "& .MuiTextField-root": { width: "100%" },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: ifasColors.surface,
                      },
                      "& .MuiFormHelperText-root": {
                        mx: 0,
                        fontSize: 11.5,
                      },
                    }}
                  >
                    {dialogMode === "MARK_EASIPOL" ? (
                      <>
                        <TextField
                          label="Uploaded By"
                          size="small"
                          value={formState.uploadedBy || ""}
                          onChange={(event) =>
                            setFormState((prev: any) => ({ ...prev, uploadedBy: event.target.value }))
                          }
                        />
                        <TextField
                          label="Uploaded At"
                          type="datetime-local"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={formState.uploadedAt || ""}
                          onChange={(event) =>
                            setFormState((prev: any) => ({ ...prev, uploadedAt: event.target.value }))
                          }
                        />
                      </>
                    ) : dialogMode === "REQUEST_APPROVAL" ? (
                      <>
                        <TextField
                          label="Requested By"
                          size="small"
                          value={formState.performedBy || ""}
                          onChange={(event) =>
                            setFormState((prev: any) => ({ ...prev, performedBy: event.target.value }))
                          }
                        />
                        <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1.5}>
                          <TextField
                            label="Requester Email (optional)"
                            size="small"
                            value={formState.requesterEmail || ""}
                            onChange={(event) =>
                              setFormState((prev: any) => ({ ...prev, requesterEmail: event.target.value }))
                            }
                          />
                          <TextField
                            label="Operations Approver Email (optional)"
                            size="small"
                            value={formState.approverEmail || ""}
                            onChange={(event) =>
                              setFormState((prev: any) => ({ ...prev, approverEmail: event.target.value }))
                            }
                            helperText="Leave blank when using the role queue."
                          />
                        </Box>
                        <TextField
                          label="Notes"
                          placeholder="Reason for transfer, bad-state note, branch agreement, or any exception context."
                          multiline
                          minRows={4}
                          size="small"
                          value={formState.notes || ""}
                          onChange={(event) =>
                            setFormState((prev: any) => ({ ...prev, notes: event.target.value }))
                          }
                        />
                        <TextField
                          label="Approval SLA (minutes)"
                          type="number"
                          size="small"
                          value={formState.slaMinutes || 120}
                          onChange={(event) =>
                            setFormState((prev: any) => ({ ...prev, slaMinutes: event.target.value }))
                          }
                          helperText="Default is 120 minutes."
                        />
                      </>
                    ) : (
                      <>
                        <TextField
                          label="Performed By"
                          size="small"
                          value={formState.performedBy || ""}
                          onChange={(event) =>
                            setFormState((prev: any) => ({ ...prev, performedBy: event.target.value }))
                          }
                        />
                        {["APPROVE_MOVEMENT", "REJECT_MOVEMENT", "ACKNOWLEDGE_MOVEMENT"].includes(
                          String(dialogMode),
                        ) && (
                          <TextField
                            label="Notes"
                            multiline
                            minRows={3}
                            size="small"
                            value={formState.notes || ""}
                            onChange={(event) =>
                              setFormState((prev: any) => ({ ...prev, notes: event.target.value }))
                            }
                          />
                        )}
                        {dialogMode === "RECEIVE_TRANSFER" && (
                          <>
                            <TextField
                              label="Receiving Storage / Shelf"
                              size="small"
                              value={formState.receivingStorageLocation || ""}
                              onChange={(event) =>
                                setFormState((prev: any) => ({ ...prev, receivingStorageLocation: event.target.value }))
                              }
                            />
                            <TextField
                              label="Mileage In"
                              type="number"
                              size="small"
                              value={formState.mileageIn || ""}
                              onChange={(event) =>
                                setFormState((prev: any) => ({ ...prev, mileageIn: event.target.value }))
                              }
                            />
                            <TextField
                              label="Receiving Supervisor Name"
                              size="small"
                              value={formState.receivingSupervisorName || ""}
                              onChange={(event) =>
                                setFormState((prev: any) => ({ ...prev, receivingSupervisorName: event.target.value }))
                              }
                            />
                            <TextField
                              label="Receiving Supervisor Signature"
                              size="small"
                              value={formState.receivingSupervisorSignature || ""}
                              onChange={(event) =>
                                setFormState((prev: any) => ({ ...prev, receivingSupervisorSignature: event.target.value }))
                              }
                            />
                            <TextField
                              label="Receiving Supervisor Date"
                              type="date"
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              value={formState.receivingSupervisorDate || ""}
                              onChange={(event) =>
                                setFormState((prev: any) => ({ ...prev, receivingSupervisorDate: event.target.value }))
                              }
                            />
                          </>
                        )}
                      </>
                    )}

                    {selectedDispatch?.dispatchType === "EXTERNAL_PARLOUR_RELEASE" && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                          border: `1px solid ${ifasColors.border}`,
                          borderRadius: 2,
                          p: 1.25,
                          backgroundColor: ifasColors.page,
                        }}
                      >
                        <FormControlLabel
                          control={<Checkbox checked={Boolean(selectedDispatch.financePaymentConfirmed)} readOnly />}
                          label="Finance confirmed"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={Boolean(selectedDispatch.branchManagerOverride)} readOnly />}
                          label="Override captured"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={Boolean(selectedDispatch.familyViewedConfirmed)} readOnly />}
                          label="Family viewed body"
                        />
                      </Box>
                    )}
                  </Box>
                </DialogContent>

                <DialogActions
                  sx={{
                    px: 2.75,
                    py: 1.9,
                    borderTop: `1px solid ${ifasColors.border}`,
                    backgroundColor: ifasColors.page,
                    gap: 1,
                  }}
                >
                  <Button
                    onClick={resetDialog}
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      color: ifasColors.text,
                      bgcolor: ifasColors.surface,
                      border: `1px solid ${ifasColors.border}`,
                      px: 2.5,
                      minWidth: 104,
                      fontWeight: 800,
                      "&:hover": { bgcolor: STANDARD_TONES.grey.bg },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={submitDialog}
                    disabled={saving}
                    variant="contained"
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      bgcolor: visual.color,
                      px: 3,
                      minWidth: 132,
                      fontWeight: 800,
                      boxShadow: `0 6px 16px ${visual.color}40`,
                      "&:hover": { bgcolor: visual.color, filter: "brightness(0.92)" },
                    }}
                  >
                    {saving ? "Saving..." : getDialogConfirmLabel(dialogMode)}
                  </Button>
                </DialogActions>
              </>
            );
          })()}
      </Dialog>
    </OperationsPageShell>
  );
}
